import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import { FileQuestion, RefreshCw } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Empty } from "@/components/empty-state";
import { FieldError } from "@/components/field-error";
import { Panel } from "@/components/panel";
import { Table } from "@/components/data-table";
import {
  decideSupplierTrustChallenge,
  refreshSupplierTrust,
  submitSupplierTrustChallenge,
  supplierTrustWorkspace,
} from "@/lib/api";
import { displayDate, titleCase } from "@/lib/utils";

type TrustWorkspace = Awaited<ReturnType<typeof supplierTrustWorkspace>>;
type Snapshot = Record<string, any>;
type Challenge = Record<string, any>;

export function SupplierRiskCenter({
  organisationId,
  canProcure,
  canManage,
}: {
  organisationId: string;
  canProcure: boolean;
  canManage: boolean;
}) {
  const [data, setData] = useState<TrustWorkspace>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [reviewing, setReviewing] = useState<Challenge>();

  const reload = useCallback(async () => {
    try {
      setError("");
      setData(await supplierTrustWorkspace(organisationId));
    } catch (reason) {
      setError((reason as Error).message);
    }
  }, [organisationId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function refresh() {
    setBusy(true);
    setError("");
    try {
      await refreshSupplierTrust(organisationId);
      await reload();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const snapshots = data?.snapshots || [];
  const challenges = data?.challenges || [];
  const methodology = data?.methodology;
  const openChallenges = challenges.filter(
    (row) => !["accepted", "rejected"].includes(row.decision),
  );

  return (
    <Panel>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold tracking-[.12em] text-[var(--text-muted)]">
            SUPPLIER RELIABILITY & RISK
          </p>
          <h2 className="mt-1 text-xl font-semibold">Evidence-based supplier risk</h2>
          <p className="mt-1 max-w-3xl text-sm text-[var(--text-secondary)]">
            Buyer-context scores use governed transaction events only. Paid plans,
            advertising and popularity never affect the result.
          </p>
        </div>
        {canProcure && (
          <Button variant="outline" disabled={busy} onClick={refresh}>
            <RefreshCw className="size-4" />
            {busy ? "Refreshing evidence…" : "Refresh evidence"}
          </Button>
        )}
      </div>

      {error && (
        <p className="mt-4 text-sm text-[var(--danger)]" role="alert">
          {error}
        </p>
      )}

      {methodology && (
        <div className="surface-subtle mt-5 grid gap-3 p-4 sm:grid-cols-3">
          <Fact label="Methodology" value={methodology.version} />
          <Fact
            label="Evidence window"
            value={`${methodology.observation_window_days} days`}
          />
          <Fact
            label="Publication"
            value={methodology.public_eligible ? "Public eligible" : "Internal only"}
          />
        </div>
      )}

      <div className="mt-5">
        {snapshots.length ? (
          <Table
            heads={[
              "Supplier",
              "Risk",
              "Score",
              "Evidence",
              "Window",
              "Challenges",
            ]}
          >
            {snapshots.map((row) => {
              const supplierChallenges = challenges.filter(
                (challenge) => challenge.vendor_id === row.vendor_id,
              );
              return (
                <tr key={row.id}>
                  <td>
                    <strong>{row.vendor_name}</strong>
                    <small className="block text-[var(--text-muted)]">
                      {titleCase(row.data_sufficiency)} evidence
                    </small>
                  </td>
                  <td>
                    <Badge value={`${row.risk_band}_risk`} />
                  </td>
                  <td className="numeric">
                    {row.score == null ? "Not rated" : Number(row.score).toFixed(1)}
                  </td>
                  <td className="numeric">
                    {row.positive_observation_count} positive ·{" "}
                    {row.adverse_observation_count} adverse
                  </td>
                  <td>
                    {displayDate(row.observation_window_start)} –{" "}
                    {displayDate(row.observation_window_end)}
                  </td>
                  <td>
                    {supplierChallenges.length ? (
                      <span className="text-sm">
                        {supplierChallenges.length} recorded ·{" "}
                        {supplierChallenges.filter(
                          (challenge) =>
                            !["accepted", "rejected"].includes(challenge.decision),
                        ).length}{" "}
                        open
                      </span>
                    ) : (
                      <span className="text-sm text-[var(--text-muted)]">None</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </Table>
        ) : (
          <Empty
            title="No reliability snapshots yet"
            description="Refresh evidence after supplier transactions have been recorded."
          />
        )}
      </div>

      <div className="mt-6 border-t border-[var(--border)] pt-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold">Supplier challenges</h3>
            <p className="text-sm text-[var(--text-secondary)]">
              Supplier statements and buyer decisions are separate append-only records.
            </p>
          </div>
          <Badge value={openChallenges.length ? "challenged" : "current"} />
        </div>
        <div className="mt-4 grid gap-3">
          {challenges.map((row) => (
            <article className="surface-subtle p-4" key={row.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <strong>{row.vendor_name}</strong>
                  <p className="mt-1 text-sm">{row.statement}</p>
                  <small className="mt-2 block text-[var(--text-muted)]">
                    {displayDate(row.created_at, true)} · {row.methodology_version}
                  </small>
                </div>
                <Badge value={row.decision || "challenged"} />
              </div>
              {row.decision_reason && (
                <p className="mt-3 border-t border-[var(--border)] pt-3 text-sm">
                  <strong>Buyer decision:</strong> {row.decision_reason}
                </p>
              )}
              {(canProcure || canManage) &&
                !["accepted", "rejected"].includes(row.decision) && (
                  <Button
                    className="mt-3"
                    size="sm"
                    variant="outline"
                    onClick={() => setReviewing(row)}
                  >
                    Review challenge
                  </Button>
                )}
            </article>
          ))}
          {!challenges.length && (
            <p className="text-sm text-[var(--text-muted)]">
              No supplier challenges have been submitted.
            </p>
          )}
        </div>
      </div>

      <ChallengeDecisionDialog
        organisationId={organisationId}
        challenge={reviewing}
        onOpenChange={(open) => !open && setReviewing(undefined)}
        onDone={reload}
      />
    </Panel>
  );
}

export function SupplierTrustPanel({
  organisationId,
  vendorId,
  workspace,
  onChanged,
}: {
  organisationId: string;
  vendorId: string;
  workspace: TrustWorkspace;
  onChanged: () => void;
}) {
  const [challenging, setChallenging] = useState(false);
  const snapshot = workspace.snapshots?.[0];
  const challenges = workspace.challenges || [];
  const factors = useMemo(
    () => factorRows(snapshot?.factor_summary),
    [snapshot?.factor_summary],
  );

  return (
    <Panel>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold tracking-[.12em] text-[var(--text-muted)]">
            YOUR RELIABILITY EVIDENCE
          </p>
          <h2 className="mt-1 text-xl font-semibold">
            Buyer-context reliability
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-[var(--text-secondary)]">
            This is visible in the current buyer relationship only. It is not a
            public Reliability Score and cannot be purchased.
          </p>
        </div>
        {snapshot && <Badge value={`${snapshot.risk_band}_risk`} />}
      </div>

      {snapshot ? (
        <>
          <div className="surface-subtle mt-4 grid gap-3 p-4 sm:grid-cols-4">
            <Fact
              label="Score"
              value={
                snapshot.score == null
                  ? "Not rated"
                  : Number(snapshot.score).toFixed(1)
              }
            />
            <Fact
              label="Evidence"
              value={`${snapshot.observation_count} observations`}
            />
            <Fact label="Method" value={snapshot.methodology_version} />
            <Fact label="Refresh due" value={displayDate(snapshot.refresh_due_at)} />
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {factors.map((factor) => (
              <div className="surface-subtle p-3 text-sm" key={factor.label}>
                <strong>{factor.label}</strong>
                <p className="mt-1 text-[var(--text-secondary)]">
                  {factor.detail}
                </p>
              </div>
            ))}
          </div>
          <Button
            className="mt-4"
            variant="outline"
            onClick={() => setChallenging(true)}
          >
            <FileQuestion className="size-4" />
            Challenge a factual input
          </Button>
        </>
      ) : (
        <Empty
          title="Not yet rated"
          description="The buyer has not generated a reliability snapshot for this supplier relationship."
        />
      )}

      {challenges.length > 0 && (
        <div className="mt-5 border-t border-[var(--border)] pt-4">
          <h3 className="font-semibold">Challenge history</h3>
          <div className="mt-3 grid gap-2">
            {challenges.map((row) => (
              <div className="surface-subtle p-3 text-sm" key={row.id}>
                <div className="flex items-start justify-between gap-3">
                  <span>{row.statement}</span>
                  <Badge value={row.decision || "challenged"} />
                </div>
                {row.decision_reason && (
                  <p className="mt-2 text-[var(--text-secondary)]">
                    Buyer response: {row.decision_reason}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <SupplierChallengeDialog
        open={challenging}
        onOpenChange={setChallenging}
        organisationId={organisationId}
        vendorId={vendorId}
        snapshot={snapshot}
        onDone={onChanged}
      />
    </Panel>
  );
}

function ChallengeDecisionDialog({
  organisationId,
  challenge,
  onOpenChange,
  onDone,
}: {
  organisationId: string;
  challenge?: Challenge;
  onOpenChange: (open: boolean) => void;
  onDone: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!challenge) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await decideSupplierTrustChallenge(
        organisationId,
        challenge.id,
        String(form.get("decision")) as
          | "information_requested"
          | "accepted"
          | "rejected",
        String(form.get("reason") || ""),
      );
      onOpenChange(false);
      await onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={Boolean(challenge)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="MAKER-CHECKER REVIEW"
          title={`Review ${challenge?.vendor_name || "supplier"} challenge`}
          description="The original score, supplier statement and buyer decision remain separate evidence."
        />
        <form className="grid gap-4 p-5" onSubmit={submit}>
          <p className="surface-subtle p-3 text-sm">{challenge?.statement}</p>
          <label className="grid gap-1 text-sm font-medium">
            Decision
            <Select name="decision" defaultValue="information_requested">
              <option value="information_requested">Request information</option>
              <option value="accepted">Accept factual challenge</option>
              <option value="rejected">Reject with evidence</option>
            </Select>
          </label>
          <label className="grid gap-1 text-sm font-medium">
            Evidence-based reason
            <Textarea
              minLength={20}
              name="reason"
              placeholder="State which source evidence was checked and why the challenge is accepted, rejected or needs more information."
              required
            />
          </label>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button disabled={busy} type="submit">
              {busy ? "Recording…" : "Record decision"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SupplierChallengeDialog({
  open,
  onOpenChange,
  organisationId,
  vendorId,
  snapshot,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  vendorId: string;
  snapshot?: Snapshot;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!snapshot) return;
    const form = new FormData(event.currentTarget);
    const evidence = String(form.get("evidence") || "")
      .split("\n")
      .map((reference) => reference.trim())
      .filter(Boolean)
      .map((reference) => ({ reference }));
    setBusy(true);
    setError("");
    try {
      await submitSupplierTrustChallenge(
        organisationId,
        vendorId,
        snapshot.id,
        String(form.get("statement") || ""),
        evidence,
      );
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="SUPPLIER CHALLENGE RIGHT"
          title="Challenge a reliability input"
          description="Identify a factual input and cite evidence. Filing a challenge never lowers the score."
        />
        <form className="grid gap-4 p-5" onSubmit={submit}>
          <label className="grid gap-1 text-sm font-medium">
            Factual statement
            <Textarea
              minLength={20}
              name="statement"
              placeholder="Explain which recorded invoice, receipt, obligation, maintenance visit or PO acknowledgement is inaccurate."
              required
            />
          </label>
          <label className="grid gap-1 text-sm font-medium">
            Evidence references
            <Textarea
              name="evidence"
              placeholder={"One reference per line\nExample: GRN-2026-0042"}
            />
          </label>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button disabled={busy || !snapshot} type="submit">
              {busy ? "Submitting…" : "Submit challenge"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <small className="block text-[var(--text-muted)]">{label}</small>
      <strong className="numeric mt-1 block">{value}</strong>
    </div>
  );
}

function factorRows(value: Record<string, any> | null | undefined) {
  if (!value) return [];
  return Object.entries(value).map(([key, factor]) => {
    const label = titleCase(key);
    if (typeof factor !== "object" || factor === null)
      return { label, detail: String(factor) };
    if ("adverse" in factor && "population" in factor)
      return {
        label,
        detail: `${factor.adverse} adverse of ${factor.population} governed events · ${factor.weight}% weight`,
      };
    if ("count" in factor)
      return {
        label,
        detail: `${factor.count} recorded · ${factor.scored ? "scored" : "context only"}`,
      };
    return { label, detail: JSON.stringify(factor) };
  });
}
